import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';

import type { HostPlayP2pClient } from '@genially/p2p-client';
import { HostPlayP2pClientEventType, PlayP2pClientEventType } from '@genially/p2p-client';
import type { PlayerData } from '@genially/p2p-lib';
import { BaseErrorCodes, PlayErrorCodes, PlayUserType } from '@genially/p2p-lib';
import { AppEnv } from '@genially/ui/build/config/appEnv';
import { FEATURE_FLAGS } from '@genially/ui/build/constants/featureFlags';
import { useFeatureFlags } from '@genially/ui/build/hooks/useFeatureFlags';

import { usePlayersLimits } from '../../../../../../bootstrap/application/components/app/components/PlayersLimitsProvider/PlayersLimitsProvider.context';
import { usePlaySessionInfo } from '../../../../../../bootstrap/application/components/app/components/PlaySessionInfoProvider/PlaySessionInfoProvider.context';
import { useLogRocketService } from '../../../../../../bootstrap/application/components/app/Routes/hooks/useLogRocketService';
import { config } from '../../../../../../bootstrap/application/config/config';
import { RouteNames } from '../../../../../../bootstrap/domain/RouteNames';
import { Ping } from '../../../../../../shared/application/components/DebugPing/Ping';
import { useFetchInitialSlide } from '../../../../../../shared/application/hooks/useFetchInitialSlide';
import { useNavigation } from '../../../../../../shared/application/hooks/useNavigation';
import { useSleep } from '../../../../../../shared/application/hooks/useSleep';
import { getLogger } from '../../../../../../shared/infrastructure/logger';
import { getToken } from '../../../../../../shared/infrastructure/tokenStore';
import { HostPreLobby } from './components/HostPreLobby/HostPreLobby';
import { createHostPlayP2pClient } from './utils/createHostPlayP2pClient';

export type HostClientContextType = {
  client: HostPlayP2pClient;
  isNewSession: boolean;
};

export const HostClientContext = createContext({});

export const MinimumDelayOfPreLobby = 2000;
export const TimeoutOfSessionCreation = 120000;

export const HostClientProvider = observer(
  ({
    params,
    children,
  }: {
    params: {
      teamId: string;
      geniallyId: string;
      sessionId?: string;
      geniallyUserId: string;
    };
    children: React.ReactNode;
  }) => {
    const client = useRef<HostPlayP2pClient | undefined>(undefined);

    // Frozen at first mount so the later `replaceUrl` call (which adds the session id to the
    // URL once the client connects) can't flip this after the fact.
    const [isNewSession] = useState(() => !params.sessionId);

    const { setUser } = usePlaySessionInfo();

    const { navigate, replaceUrl } = useNavigation();

    const { trackWithSamplingOnce } = useLogRocketService();

    const featureFlags = useFeatureFlags();

    // In open mode the host goes straight into the game, so there is nothing to pad the wait for.
    const isOpenMode = !!featureFlags?.includes(FEATURE_FLAGS.FEAT_LT4_PLAY_OPEN_MODE);

    const minimumDelayOfPreLobby = useSleep(isOpenMode ? 0 : MinimumDelayOfPreLobby);
    const timeoutOfSessionCreation = useSleep(TimeoutOfSessionCreation);

    const [connecting, setConnecting] = useState(false);

    const initialSlideFetch = useFetchInitialSlide(params.geniallyId);

    const { limitPerSession } = usePlayersLimits();

    const isReady = useCallback(
      (
        clientToAssert: HostPlayP2pClient | undefined,
      ): clientToAssert is HostPlayP2pClient => {
        return (
          !connecting &&
          !!clientToAssert &&
          !!initialSlideFetch.data &&
          !minimumDelayOfPreLobby.running
        );
      },
      [connecting, initialSlideFetch.data, minimumDelayOfPreLobby.running],
    );

    useEffect(() => {
      if (connecting) return;
      if (!initialSlideFetch.data) return;
      if (client.current) return;

      const { geniallyId, sessionId, teamId, geniallyUserId } = params;

      const newClient = createHostPlayP2pClient({
        geniallyId,
        teamId,
        geniallyUserId,
        previousSessionId: sessionId,
        initialSlide: initialSlideFetch.data,
        featureFlags,
        playersLimitPerSession: limitPerSession,
        authToken: getToken(),
      });

      newClient.on(PlayP2pClientEventType.Error, code => {
        if (code === PlayErrorCodes.CannotReplaceHost) {
          replaceUrl({
            to: RouteNames.PlayerSessionFinder,
          });
        } else if (code === BaseErrorCodes.Unauthorized) {
          replaceUrl({
            to: RouteNames.NoGeniallyAccess,
          });
        } else {
          getLogger().error('Redirecting to /500', {
            errorCode: code,
            geniallyId,
            sessionId,
            page: 'HostClientProvider (PlayP2pClientEventType.Error)',
          });

          replaceUrl({
            to: RouteNames.UnknownError,
          });
        }
      });

      newClient.on(HostPlayP2pClientEventType.HostReplaced, () => {
        newClient.dispose();

        replaceUrl({
          to: RouteNames.PlayerSessionFinder,
        });
      });

      const onGameSync = () => {
        setConnecting(false);
        timeoutOfSessionCreation.stop();

        // Starting here rather than on mount guarantees the server connection is up, so the
        // one-shot `when(() => game.isStarted)` observer in HostPlayP2pClient actually sends.
        // The guard matters on host reload: setIsStarted() resets startedAt, which would
        // corrupt the session duration of a game whose state was recovered from a player.
        if (isOpenMode && !newClient.game.isStarted) {
          newClient.startGame();
        }

        newClient.off(HostPlayP2pClientEventType.GameSync, onGameSync);
      };

      newClient.on(HostPlayP2pClientEventType.GameSync, onGameSync);

      const track = ({ allPlayers }: { allPlayers: PlayerData[] }) => {
        if (allPlayers.length > 0) {
          const tracked = trackWithSamplingOnce('play_host', 10);

          if (tracked) {
            newClient.markAsTrackedInLogRocket();
          }

          newClient.off(HostPlayP2pClientEventType.PlayersJoined, track);
        }
      };

      newClient.on(HostPlayP2pClientEventType.PlayersJoined, track);

      setUser({
        type: PlayUserType.Host,
        geniallyId,
        teamId,
        sessionId: newClient.sessionId,
      });

      setConnecting(true);
      client.current = newClient;

      if (config.appEnv === AppEnv.Local) {
        window.resetGame = () => {
          client.current?.resetGame();
        };
      }

      newClient.start();
    }, [
      trackWithSamplingOnce,
      connecting,
      initialSlideFetch.data,
      params,
      timeoutOfSessionCreation,
      replaceUrl,
      featureFlags,
      isOpenMode,
      limitPerSession,
      setUser,
    ]);

    useEffect(() => {
      return () => {
        if (client.current) {
          client.current.dispose();

          window.resetGame = undefined;
        }
      };
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      if (timeoutOfSessionCreation.ended) {
        getLogger().error('Redirecting to /500', {
          geniallyId: params.geniallyId,
          sessionId: params.sessionId,
          page: 'HostClientProvider (timeoutOfSessionCreation.ended)',
          running: timeoutOfSessionCreation.running,
          ended: timeoutOfSessionCreation.ended,
          stopped: timeoutOfSessionCreation.stopped,
        });
        navigate({ to: RouteNames.UnknownError });
      }
    }, [navigate, timeoutOfSessionCreation]);

    useEffect(() => {
      return () => {
        Ping.reset();
      };
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      if (client.current?.pingTime) {
        Ping.value = client.current.pingTime;
      }
    }, [client, client.current?.pingTime]);

    if (!isReady(client.current)) {
      return <HostPreLobby />;
    }

    const contextValue: HostClientContextType = {
      client: client.current,
      isNewSession,
    };

    return (
      <HostClientContext.Provider value={contextValue}>
        {children}
      </HostClientContext.Provider>
    );
  },
);
